import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../api/client';
import { useApi } from '../api/use-api';
import { createSession } from '../api/sessions';
import {
  readCodecPref, readEncodingPref, readResolutionPref,
  readFpsPref, readJpegQualityPref,
} from '../session/encoding-pref';
import { listHosts, type HostSummary } from '../api/hosts';
import { ApprovalWaiting } from '../components/ApprovalWaiting';
import { Button } from '../components/Button';
import { ErrorPanel, type ErrorKind } from '../components/ErrorPanel';
import { Spinner } from '../components/Spinner';

type ResolvedError = { kind: ErrorKind; message: string };

type State =
  | { kind: 'init' }
  | { kind: 'requesting'; host: HostSummary }
  | { kind: 'error'; error: ResolvedError };

function resolveError(err: unknown): ResolvedError {
  if (err instanceof ApiError) {
    if (err.status === 408) {
      return {
        kind: 'timeout',
        message: "Host didn't respond. It may have gone offline — refresh the host list and try again.",
      };
    }
    if (err.status === 403) {
      const body = (err.body as { error?: string }) ?? {};
      if (body.error === 'APPROVAL_DENIED') {
        return {
          kind: 'denied',
          message: "Host denied the connection. Whoever's at the host machine declined.",
        };
      }
      if (body.error === 'IP_NOT_ALLOWED') {
        return {
          kind: 'forbidden',
          message: "Your IP isn't on this host's allowlist. Add it from the host's configuration.",
        };
      }
    }
    if (err.status === 409) {
      return {
        kind: 'offline',
        message: 'Host is offline. Start the host agent on that machine, then try again.',
      };
    }
    if (err.status === 410) {
      return {
        kind: 'revoked',
        message: 'This host has been revoked. Re-create it from the host list and re-pair.',
      };
    }
    if (err.status === 422) {
      const body = (err.body as { error?: string; codec?: string; reason?: string }) ?? {};
      if (body.error === 'CODEC_UNAVAILABLE') {
        return {
          kind: 'codec',
          message: `This host can't encode ${body.codec ?? 'the selected codec'} right now (${body.reason ?? 'unknown'}). Switch the codec selector to JPEG and retry.`,
        };
      }
    }
  }
  return {
    kind: 'generic',
    message: "Couldn't start the session. Check the host is online and try again.",
  };
}

export function ConnectPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const hostId = params.get('hostId');
  const [state, setState] = useState<State>({ kind: 'init' });
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!hostId) {
      setState({ kind: 'error', error: { kind: 'generic', message: 'Missing host id.' } });
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    abortRef.current = controller;

    void (async () => {
      try {
        const all = await listHosts(api);
        const host = all.hosts.find((h) => h.id === hostId);
        if (!host) { setState({ kind: 'error', error: { kind: 'generic', message: 'Host not found.' } }); return; }
        if (cancelled) return;
        setState({ kind: 'requesting', host });

        const created = await createSession(
          api,
          {
            hostId, monitorId: 0,
            encoding: readEncodingPref(),
            resolution: readResolutionPref(),
            codec: readCodecPref(),
            fps: readFpsPref(),
            jpegQuality: readJpegQualityPref(),
          },
          controller.signal,
        );
        if (cancelled) return;
        const target = `/session/${created.sessionId}`;
        const search = new URLSearchParams({
          mediaToken: created.mediaToken,
          hostName: host.name,
          codec: created.codec,
        });
        void navigate(`${target}?${search.toString()}`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') {
          void navigate('/hosts', { replace: true });
          return;
        }
        setState({ kind: 'error', error: resolveError(err) });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [api, hostId, navigate]);

  if (state.kind === 'init') {
    return (
      <main className="grid h-screen place-items-center">
        <Spinner className="h-6 w-6 text-text-muted" />
      </main>
    );
  }
  if (state.kind === 'requesting') {
    return <ApprovalWaiting hostName={state.host.name} onCancel={() => abortRef.current?.abort()} />;
  }
  return (
    <main className="grid h-screen place-items-center px-4">
      <ErrorPanel
        kind={state.error.kind}
        message={state.error.message}
        action={
          <Button variant="secondary" onClick={() => { void navigate('/hosts', { replace: true }); }}>
            Back to hosts
          </Button>
        }
      />
    </main>
  );
}
